import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, ArrowRight, UtensilsCrossed, Leaf, ChefHat, Folder, Ban } from 'lucide-react';
import { getPOSSalesItemMappings } from '@/lib/posSalesItemMapping';
import { PLATFORMS, platformDisplayName } from '@/lib/platformConstants';
import toastLogo from '@/assets/toast-logo.png';
import doordashLogo from '@/assets/doordash-logo.png';
import uberEatsLogo from '@/assets/uber-eats-logo.svg';

interface POSReportsStep3ReviewProps {
  onNext: () => void;
  onPrevious: () => void;
  platform?: string | null;
}

interface TrainedMapping {
  id: string;
  platform: string;
  pos_item_name: string;
  pos_item_identifier: string;
  mapped_type: string;
  mapped_item_id: string | null;
  mapped_item_name: string | null;
  auto_matched: boolean;
  confidence?: number;
}

const PLATFORM_LOGOS: Record<string, string> = {
  toast: toastLogo,
  doordash: doordashLogo,
  ubereats: uberEatsLogo,
};

const getMappingTypeIcon = (type: string) => {
  switch (type) {
    case 'menu_item':
      return <UtensilsCrossed className="h-4 w-4 text-blue-600" />;
    case 'ingredient':
      return <Leaf className="h-4 w-4 text-green-600" />;
    case 'prep_item':
      return <ChefHat className="h-4 w-4 text-purple-600" />;
    case 'menu_category':
      return <Folder className="h-4 w-4 text-indigo-600" />;
    case 'non_menu':
      return <Ban className="h-4 w-4 text-gray-600" />;
    default:
      return null;
  }
};

const getMappingTypeLabel = (type: string) => {
  switch (type) {
    case 'menu_item':
      return 'Menu Item';
    case 'ingredient':
      return 'Ingredient';
    case 'prep_item':
      return 'Prep Item';
    case 'menu_category':
      return 'Menu Category';
    case 'non_menu':
      return 'Non-Menu';
    case 'ignored':
      return 'Ignored';
    default:
      return type;
  }
};

export const POSReportsStep3Review = ({ onNext, onPrevious, platform }: POSReportsStep3ReviewProps) => {
  const { toast } = useToast();
  const [mappings, setMappings] = useState<TrainedMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [activePlatforms, setActivePlatforms] = useState<string[]>([]);

  useEffect(() => {
    loadMappings();
  }, [platform]);

  const loadMappings = async () => {
    try {
      setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      // Get platforms to load
      let platformsToLoad: string[] = [];
      
      if (platform) {
        platformsToLoad = [platform];
      } else {
        const { data: integrations, error: intError } = await supabase
          .from('company_integrations')
          .select('integration_type')
          .eq('company_id', companyId)
          .in('integration_type', [PLATFORMS.TOAST, PLATFORMS.DOORDASH, 'ubereats'])
          .eq('is_active', true);
        
        if (intError) throw intError;
        
        platformsToLoad = (integrations || []).map(i => i.integration_type);
      }

      setActivePlatforms(platformsToLoad);

      // Load mappings for all platforms
      const allMappings: TrainedMapping[] = [];
      
      for (const plat of platformsToLoad) {
        const platformMappings = await getPOSSalesItemMappings(companyId, plat);
        
        // Load mapped item names based on type
        const mappingsWithNames = await Promise.all(
          platformMappings
            .filter(m => m.mapped_type !== 'ignored' && m.mapped_type !== 'non_menu' && m.mapped_item_id)
            .map(async (m) => {
              let mappedItemName: string | null = null;
              
              try {
                if (m.mapped_type === 'menu_item') {
                  const { data } = await supabase
                    .from('menu_items')
                    .select('name')
                    .eq('id', m.mapped_item_id)
                    .single();
                  mappedItemName = data?.name || null;
                } else if (m.mapped_type === 'ingredient') {
                  const { data } = await supabase
                    .from('ingredients')
                    .select('name')
                    .eq('id', m.mapped_item_id)
                    .single();
                  mappedItemName = data?.name || null;
                } else if (m.mapped_type === 'prep_item') {
                  const { data } = await supabase
                    .from('prep_items')
                    .select('name')
                    .eq('id', m.mapped_item_id)
                    .single();
                  mappedItemName = data?.name || null;
                } else if (m.mapped_type === 'menu_category') {
                  const { data } = await supabase
                    .from('menu_categories')
                    .select('name')
                    .eq('id', m.mapped_item_id)
                    .single();
                  mappedItemName = data?.name || null;
                }
              } catch (error) {
                console.error(`Error loading mapped item name for ${m.mapped_type}:`, error);
              }
              
              return {
                id: `${m.platform}-${m.pos_item_identifier}`,
                platform: m.platform,
                pos_item_name: m.pos_item_name,
                pos_item_identifier: m.pos_item_identifier,
                mapped_type: m.mapped_type,
                mapped_item_id: m.mapped_item_id,
                mapped_item_name: mappedItemName,
                auto_matched: m.auto_matched,
                confidence: m.confidence,
              };
            })
        );
        
        // Also include ignored/non-menu items
        const otherMappings = platformMappings
          .filter(m => m.mapped_type === 'ignored' || m.mapped_type === 'non_menu')
          .map(m => ({
            id: `${m.platform}-${m.pos_item_identifier}`,
            platform: m.platform,
            pos_item_name: m.pos_item_name,
            pos_item_identifier: m.pos_item_identifier,
            mapped_type: m.mapped_type,
            mapped_item_id: null,
            mapped_item_name: null,
            auto_matched: m.auto_matched,
            confidence: m.confidence,
          }));
        
        allMappings.push(...mappingsWithNames, ...otherMappings);
      }

      setMappings(allMappings);
    } catch (error: any) {
      console.error('Error loading mappings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load mappings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-64" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group mappings by platform
  const mappingsByPlatform = mappings.reduce((acc, mapping) => {
    if (!acc[mapping.platform]) {
      acc[mapping.platform] = [];
    }
    acc[mapping.platform].push(mapping);
    return acc;
  }, {} as Record<string, TrainedMapping[]>);

  // Calculate summary stats
  const totalMappings = mappings.length;
  const mappingsByType = mappings.reduce((acc, m) => {
    acc[m.mapped_type] = (acc[m.mapped_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Review POS Item Mappings</h3>
        <p className="text-sm text-muted-foreground">
          Review all the POS items you've mapped. You can proceed to complete the training.
        </p>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">{totalMappings}</div>
              <div className="text-sm text-muted-foreground">Total Mappings</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{mappingsByType.menu_item || 0}</div>
              <div className="text-sm text-muted-foreground">Menu Items</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{mappingsByType.ingredient || 0}</div>
              <div className="text-sm text-muted-foreground">Ingredients</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{mappingsByType.menu_category || 0}</div>
              <div className="text-sm text-muted-foreground">Categories</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {mappings.length === 0 ? (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-4">
              <UtensilsCrossed className="h-16 w-16 text-amber-600 mx-auto" />
              <div>
                <h4 className="font-semibold mb-2 text-lg">No Mappings Yet</h4>
                <p className="text-muted-foreground">Please complete the mapping step first.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(mappingsByPlatform).map(([platformId, platformMappings]) => {
            const logo = PLATFORM_LOGOS[platformId];
            if (!logo) return null;
            const platformTitle = platformDisplayName(platformId);

            return (
              <Card key={platformId}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <img
                        src={logo}
                        alt={platformTitle}
                        className="h-8 w-8 object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <div>
                        <CardTitle>{platformTitle}</CardTitle>
                        <CardDescription>
                          {platformMappings.length} mapped item{platformMappings.length !== 1 ? 's' : ''}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>POS Item</TableHead>
                          <TableHead>Mapping Type</TableHead>
                          <TableHead>Mapped To</TableHead>
                          <TableHead>Auto-Matched</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {platformMappings.map((mapping) => (
                          <TableRow key={mapping.id}>
                            <TableCell className="font-medium">{mapping.pos_item_name}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getMappingTypeIcon(mapping.mapped_type)}
                                <Badge variant="outline">
                                  {getMappingTypeLabel(mapping.mapped_type)}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              {mapping.mapped_item_name ? (
                                <span>{mapping.mapped_item_name}</span>
                              ) : (
                                <span className="text-muted-foreground italic">
                                  {mapping.mapped_type === 'ignored' ? 'Ignored' : 'Non-Menu Item'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {mapping.auto_matched ? (
                                <div className="flex items-center gap-1">
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  <span className="text-sm text-muted-foreground">
                                    {mapping.confidence ? `${Math.round(mapping.confidence * 100)}%` : 'Yes'}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">Manual</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          Previous Step
        </Button>
        <Button onClick={onNext}>
          Continue to Complete
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};
